import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';

class NaFreeSwatchesDrawer extends Component {
    /** @type {Element[]} */
    #addButtons = [];
    /** @type {(() => void) | null} */
    #cartUpdateHandler = null;

    connectedCallback() {
        super.connectedCallback();

        // Find add to cart buttons within this component
        this.#addButtons = Array.from(this.querySelectorAll('.category-add-to-cart'));

        // Initial setup for cart button states
        this.#addButtons.forEach((btn) => this.checkCart(btn));

        // Listen for global cart updates
        this.#cartUpdateHandler = () => {
            this.#addButtons.forEach((btn) => this.checkCart(btn));
        };
        document.addEventListener('cart:update', this.#cartUpdateHandler);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.#cartUpdateHandler) {
            document.removeEventListener('cart:update', this.#cartUpdateHandler);
        }
    }

    async getCart() {
        const res = await fetch('/cart.js');
        if (!res.ok) throw new Error(`Failed to fetch cart: ${res.status}`);
        return res.json();
    }

    async handleAddToCart(event, target) {
        const button =
            target || (event.target && event.target.closest && event.target.closest('.category-add-to-cart'));
        if (!button) return;
        event.preventDefault();
        event.stopPropagation();

        const variantId = button.dataset.variantId;
        if (!variantId) return;

        try {
            button.innerText = 'Adding...';
            button.disabled = true;

            // Audit note: intentional cross-component lookup (do not flag) — cart sections live
            // outside this component's subtree, so this.refs cannot reach them.
            const cartComponents = document.querySelectorAll('cart-items-component');
            const sectionIds = [];
            cartComponents.forEach((el) => {
                if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
            });

            const res = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify({
                    id: Number(variantId),
                    quantity: 1,
                }),
            });

            if (!res.ok) throw new Error('Add failed');

            const data = await res.json();
            if (data.status) throw new Error(data.message);

            const cartRes = await fetch('/cart?sections=' + sectionIds.join(','));
            if (!cartRes.ok) throw new Error('Failed to fetch updated cart sections');

            document.dispatchEvent(
                new CartAddEvent({
                    item: data,
                    source: 'custom-add-to-cart',
                })
            );

            customElements.whenDefined('cart-drawer-component').then(() => {
                // Audit note: intentional cross-component lookup (do not flag) — the cart drawer
                // lives in the header group, outside this component's subtree.
                const cartDrawer = document.querySelector('cart-drawer-component');
                if (cartDrawer) {
                    cartDrawer.dispatchEvent(new DialogOpenEvent());
                }
            });

            button.innerText = 'Added';

            setTimeout(async () => {
                const cart = await this.getCart();
                const exists = cart.items.some((item) => item.variant_id === Number(variantId));

                if (exists) {
                    button.innerText = 'Already in Cart';
                    button.disabled = true;
                }
            }, 500);
        } catch (e) {
            console.error(e);
            button.innerText = 'Error';
            button.disabled = false;
        }
    }

    async checkCart(button) {
        const variantId = button.dataset.variantId;
        if (!variantId) return;

        try {
            const cart = await this.getCart();
            const exists = cart.items.some((item) => item.id === Number(variantId));

            if (exists) {
                button.innerText = 'Already in Cart';
                button.disabled = true;
            } else {
                button.innerText = 'Add to Cart';
                button.disabled = false;
            }
        } catch (e) {
            console.error(e);
        }
    }
}

if (!customElements.get('na-free-swatches-drawer-component')) {
    customElements.define('na-free-swatches-drawer-component', NaFreeSwatchesDrawer);
}
